"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.IpmClient = void 0;
// eslint-disable-next-line import/no-extraneous-dependencies
const insights_client_library_1 = require("@eccr-cuttingedge/insights-client-library");
const IpmRulesEngine_1 = require("./IpmRulesEngine");
const utils_1 = require("./utils");
const Logger_1 = __importDefault(require("../Logger"));
const WorkerTypes_1 = require("./WorkerTypes");
/**
 * Client-side interface for inter-product-messaging features
 */
class IpmClient {
    onUserConsentInitializeRulesEngine() {
        return __awaiter(this, void 0, void 0, function* () {
            Logger_1.default.debug("IPM: user consent flow initiated from SDK");
            if (!(0, utils_1.oxygenIdFrom)(this.analytics)) {
                Logger_1.default.debug("IPM: Unnamed user");
                return;
            }
            if (this.rulesEngines.length) {
                Logger_1.default.debug("IPM: Rules engines already loaded");
                return;
            }
            Logger_1.default.debug("IPM: Fetching rules engines");
            this.rulesEngines = yield (0, IpmRulesEngine_1.loadAndInitializeRulesEngines)(this.analytics, this.messageService);
            this.rulesEngines.forEach((engine, i) => {
                Logger_1.default.debug(`IPM: [Engine-${i}] Listening for messages`);
                engine.on(WorkerTypes_1.WorkerToIpmEventType.MESSAGE_GENERATED, (message) => __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    Logger_1.default.debug(`IPM: [Engine-${i}] Worker raw message`, message);
                    if (message.binaryPayload) {
                        const uint8Array = new Uint8Array(Object.values(message.binaryPayload));
                        const generatedMessage = insights_client_library_1.InsightProtoExportInterface.Insight.deserializeBinary(uint8Array);
                        Logger_1.default.debug(`IPM: [Engine-${i}] Restored GeneratedMessage`, generatedMessage);
                        this.messageService.submitMessage(generatedMessage).then((result) => __awaiter(this, void 0, void 0, function* () {
                            Logger_1.default.debug(`IPM: [Engine-${i}] Submit GeneratedMessage result`, yield result.json());
                        }));
                    }
                    (_a = this.hooks.messageGenerated) === null || _a === void 0 ? void 0 : _a.forEach((callback) => callback(message));
                }));
            });
        });
    }
    constructor(analytics, messageService) {
        this.analytics = analytics;
        this.messageService = messageService;
        this.rulesEngines = [];
        this.hooks = {};
        analytics.on("user_consent", this.onUserConsentInitializeRulesEngine.bind(this));
        analytics.on("track", (_, message) => {
            if (!message) {
                return;
            }
            // No support for unnamed user for now
            if (!(0, utils_1.oxygenIdFrom)(analytics)) {
                return;
            }
            this.rulesEngines.forEach((engine) => {
                engine.push({
                    type: WorkerTypes_1.IpmToWorkerEventType.TRACK,
                    payload: JSON.stringify(message),
                });
            });
        });
    }
    /**
     * Registers a callback (hook) for a specific event on the IPM client.
     *
     * @template K - The key of the event type in `EventPayloads`.
     *
     * @param {K} event - The name of the event to listen for. Currently, "messageGenerated" is supported,
     * indicating that a message has been generated from the rules engine.
     * @param {(payload: IpmEventPayloads[K]) => void} callback - The callback function to execute when the event is triggered.
     * The `payload` parameter is the specific data structure for the event type.
     * Refer to {@link IpmEventPayloads} for more information.
     *
     * @example
     * client.on('messageGenerated', (payload) => {
     *   console.log('Generated message ID:', payload.id);
     *   console.log('Generated message JSON Payload:', payload.jsonPayload);
     * });
     */
    on(event, callback) {
        if (!this.hooks[event]) {
            this.hooks[event] = [];
        }
        this.hooks[event].push(callback);
        return this;
    }
    /**
     * Fetches a single message (insight) from the Insights service.
     * @param filter - The variables to the `getInsight` GraphQL query provided by Insights service.
     * @returns A promise that resolves to the `Insight` data returned by the `getInsight` GraphQL query.
     *
     * @example
     * const fetchedInsight = await client.getMessage({ insightId: "insight-id" });
     * console.log('Fetched insight:', fetchedInsight);
     * renderInsightMessage(fetchedInsight);
     */
    getMessage(filter) {
        return this.messageService.getMessage(filter);
    }
    /**
     * Fetches list of messages (insights) from the Insights service.
     * @param filter - The variables to the `getInsights` GraphQL query provided by Insights service
     * @returns A promise that resolves to the `PaginatedInsights` data returned by the `getInsights` GraphQL query.
     *
     * @example
     * const paginatedInsights = await client.getMessage({ scope: "user" });
     * console.log('Fetched paginated insights:', paginatedInsights);
     * renderInsightMessages(paginatedInsights);
     */
    getMessages(filter) {
        return this.messageService.getMessages(filter);
    }
}
exports.IpmClient = IpmClient;
